/**
 * Инструменты подготовки к играм (KAN-834, KAN-841, KAN-849).
 *
 * Единственный источник состава раздела: отсюда его берут страница-хаб,
 * записи в глобальном поиске (`pages.ts`) и колесо тулзов (KAN-867).
 * Иконки намеренно НЕ здесь: модуль нужен и там, где нужен только путь
 * инструмента, без компонентов иконок. С KAN-971 иконки везде из
 * `lucide-react` — вынести их сюда можно, если понадобится одно место.
 */

export type ToolId = "compare" | "tiermaker" | "scouting";

/**
 * `ready` — инструмент работает. `planned` — маршрут есть, внутри заглушка.
 * Скрывать запланированные нельзя: состав раздела не должен меняться
 * от релиза к релизу, иначе им нельзя пользоваться как ориентиром.
 */
export type ToolStatus = "ready" | "planned";

export interface ToolLink {
  id: ToolId;
  path: string;
  /** Ключ в неймспейсе `Tools.items.<id>`: `.title` и `.description`. */
  i18nKey: ToolId;
  /** Слова для глобального поиска: обе локали в одной строке. */
  keywords: string;
  status: ToolStatus;
}

export const TOOLS_ROOT = "/tools";

export const tools: ToolLink[] = [
  {
    id: "compare",
    path: `${TOOLS_ROOT}/compare`,
    i18nKey: "compare",
    keywords:
      "сравнение игроков чемпионы винрейт compare players champions winrate opgg",
    status: "ready",
  },
  {
    id: "tiermaker",
    path: `${TOOLS_ROOT}/tiermaker`,
    i18nKey: "tiermaker",
    keywords:
      "тирмейкер тирлист пул чемпионов роли tiermaker tier list champion pool draft",
    status: "ready",
  },
  {
    id: "scouting",
    path: `${TOOLS_ROOT}/scouting`,
    i18nKey: "scouting",
    keywords:
      "разбор соперника скаутинг команда пики баны scouting enemy team picks bans",
    status: "ready",
  },
];

/**
 * Параметры адреса полных страниц (KAN-838, KAN-851). Здесь, а не в хуках
 * страниц: мини-модалки колеса (KAN-868) собирают тот же адрес, и разойтись
 * они не должны — «открыть полностью» обязан открыть ровно то же состояние.
 */
export const TOOL_URL_PARAMS = {
  compare: { users: "users", source: "source" },
  scouting: { team: "team", matches: "matches" },
  tiermaker: { list: "list" },
} as const;

/** Состояние мини-версии, которое переносится в полную страницу. */
export type ToolUrlState =
  | { tool: "compare"; users: string[]; source?: string }
  | { tool: "scouting"; team?: string | null; matches?: string[] }
  | { tool: "tiermaker"; list?: string | null };

export function toolHref(state: ToolUrlState): string {
  const path = tools.find((t) => t.id === state.tool)?.path ?? TOOLS_ROOT;
  const params = new URLSearchParams();
  if (state.tool === "compare") {
    const p = TOOL_URL_PARAMS.compare;
    if (state.users.length) params.set(p.users, state.users.join(","));
    if (state.source) params.set(p.source, state.source);
  } else if (state.tool === "scouting") {
    const p = TOOL_URL_PARAMS.scouting;
    if (state.team) params.set(p.team, state.team);
    if (state.matches?.length) params.set(p.matches, state.matches.join(","));
  } else if (state.list) {
    params.set(TOOL_URL_PARAMS.tiermaker.list, state.list);
  }
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}
